import * as THREE from 'three';

// Pulsing "you are here" beacon for wallet search. Attached as a child of the
// stars Points object so it inherits the group rotation of non-spiral views
// and always sits exactly where the star would be.

const makeHaloTexture = (): THREE.Texture => {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(0, 204, 255, 0.9)');
    g.addColorStop(0.35, 'rgba(0, 204, 255, 0.35)');
    g.addColorStop(1, 'rgba(0, 204, 255, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
};

export class SearchMarkerSystem {
  private root: THREE.Group | null = null;
  private core: THREE.Mesh | null = null;
  private halo: THREE.Sprite | null = null;
  private t = 0;

  set(parent: THREE.Object3D, pos: { x: number; y: number; z: number }): void {
    if (!this.root) {
      this.root = new THREE.Group();

      this.core = new THREE.Mesh(
        new THREE.SphereGeometry(0.9, 16, 16),
        new THREE.MeshBasicMaterial({
          color: 0x00ccff,
          transparent: true,
          opacity: 0.95,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );

      this.halo = new THREE.Sprite(
        new THREE.SpriteMaterial({
          map: makeHaloTexture(),
          color: 0x00ccff,
          transparent: true,
          opacity: 0.85,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        })
      );
      this.halo.scale.setScalar(10);

      this.root.add(this.core);
      this.root.add(this.halo);
    }

    if (this.root.parent !== parent) {
      this.root.parent?.remove(this.root);
      parent.add(this.root);
    }
    this.root.position.set(pos.x, pos.y, pos.z);
    this.root.visible = true;
    this.t = 0;
  }

  tick(dt: number): void {
    if (!this.root || !this.root.visible) return;
    this.t += dt;
    const pulse = 1 + 0.28 * Math.sin(this.t * 3.2);
    this.core?.scale.setScalar(pulse);
    if (this.halo) {
      this.halo.scale.setScalar(10 * (1 + 0.18 * Math.sin(this.t * 3.2 + 1.2)));
      (this.halo.material as THREE.SpriteMaterial).opacity =
        0.55 + 0.3 * (0.5 + 0.5 * Math.sin(this.t * 2.1));
    }
  }

  clear(): void {
    if (this.root) this.root.visible = false;
  }

  destroy(): void {
    if (!this.root) return;
    this.root.parent?.remove(this.root);
    this.core?.geometry.dispose();
    (this.core?.material as THREE.Material | undefined)?.dispose();
    const haloMat = this.halo?.material as THREE.SpriteMaterial | undefined;
    haloMat?.map?.dispose();
    haloMat?.dispose();
    this.root = null;
    this.core = null;
    this.halo = null;
  }
}
